import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, BadgeCheck, ArrowDown, Play } from "lucide-react";
import { IMAGES, STATS } from "./data";
import { useSite } from "@/lib/site";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { content } = useSite();
  const stats = content.stats?.length ? content.stats : STATS;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 pb-20 sm:pt-40 lg:pb-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-6 font-outfit text-xs sm:text-sm font-semibold uppercase tracking-[0.3em] text-terra"
            data-testid="hero-eyebrow"
          >
            {content.settings?.hero_eyebrow || "Lactation · Birth Education · Infant Feeding"}
          </motion.p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.08] tracking-tight text-ink" data-testid="hero-headline">
            <Line delay={0.25}>{content.settings?.hero_line1 || "Nurturing every birth."}</Line>
            <Line delay={0.4}>
              <span className="italic text-terra">{content.settings?.hero_line2 || "Empowering"}</span>
            </Line>
            <Line delay={0.55}>{content.settings?.hero_line3 || "every mother."}</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-7 max-w-xl text-base text-plumsoft leading-relaxed sm:text-lg"
            data-testid="hero-subtext"
          >
            {content.settings?.hero_subtext ||
              "Evidence-based lactation consulting and childbirth education by Yogyata Wadhwa, IBCLC — supporting 20,000+ families in Gurgaon and worldwide."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-book-cta"
              onClick={() => onBook()}
              className="group rounded-full bg-terra px-7 py-3.5 font-outfit text-sm font-semibold text-white transition-all duration-300 hover:bg-terradark hover:shadow-deep"
            >
              Book a consultation
              <span className="ml-2 inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
            <a
              data-testid="hero-courses-cta"
              href="#courses"
              onClick={(e) => {
                e.preventDefault();
                window.__lenis?.scrollTo("#courses", { offset: -80 });
              }}
              className="rounded-full border border-ink/20 px-7 py-3.5 font-outfit text-sm font-semibold text-ink transition-colors duration-300 hover:border-terra hover:text-terra"
            >
              Explore courses
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <span data-testid="hero-pill-ibclc" className="inline-flex items-center gap-2 rounded-full bg-cream px-4 py-2 text-xs font-outfit font-medium text-plumsoft">
              <BadgeCheck className="h-3.5 w-3.5 text-sage" /> IBCLC & Feeding Therapist
            </span>
            <span data-testid="hero-pill-location" className="inline-flex items-center gap-2 rounded-full bg-cream px-4 py-2 text-xs font-outfit font-medium text-plumsoft">
              <MapPin className="h-3.5 w-3.5 text-terra" /> Gurgaon, India · Online worldwide
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.35 }}
            className="mt-10 grid grid-cols-2 gap-x-6 gap-y-6 border-t border-ink/10 pt-8 lg:grid-cols-4"
            data-testid="hero-stats"
          >
            {stats.map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <p className="font-display text-3xl font-semibold text-terra sm:text-4xl">{s.value}</p>
                <p className="mt-1 font-outfit text-[11px] uppercase tracking-[0.14em] text-faint sm:text-xs">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="overflow-hidden rounded-[2rem] rounded-tr-[7rem] shadow-deep">
              <img
                src={content.settings?.hero_image_url || IMAGES.hero}
                alt="Mother holding her newborn in warm light"
                className="h-[300px] w-full object-cover sm:h-[360px] lg:h-[480px]"
                data-testid="hero-image"
              />
            </motion.div>
            <motion.div
              style={{ y: cardY }}
              className="absolute -bottom-8 -left-4 sm:-left-10 rounded-2xl border border-white/60 bg-sand/85 p-5 shadow-deep backdrop-blur-xl"
              data-testid="hero-floating-card"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-terra/10">
                  <Play className="h-4 w-4 fill-terra text-terra" />
                </span>
                <div>
                  <p className="font-outfit text-sm font-semibold text-ink">Evidence Based Birth</p>
                  <p className="text-xs text-faint">Podcast · Episode 402</p>
                </div>
              </div>
            </motion.div>
            <motion.img
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 1 }}
              src={IMAGES.heroAlt}
              alt="Yogyata Wadhwa — founder of The Birth Life"
              className="absolute -top-8 -right-4 hidden w-36 rotate-3 rounded-2xl border-4 border-sand object-cover shadow-lift sm:block sm:w-44"
              data-testid="hero-image-secondary"
            />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="mt-16 flex justify-center"
      >
        <ArrowDown className="h-5 w-5 animate-bounce text-faint" />
      </motion.div>
    </section>
  );
};
